import React, { useState } from 'react';
import { ArrowLeft, ShieldAlert, Battery, BatteryFull, BatteryLow } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, Cell } from 'recharts';

interface Props {
  onBack: () => void;
}

export const EmergencyFundScreen: React.FC<Props> = ({ onBack }) => {
  const [expenses, setExpenses] = useState(3000);
  const [savings, setSavings] = useState(4500);

  // Safety check for division by zero
  const monthsCovered = expenses > 0 ? savings / expenses : 0;
  const daysCovered = Math.floor(monthsCovered * 30);
  
  // Data for chart
  const data = [
    { name: 'Your Fund', value: savings },
  ];

  const getStatusColor = () => {
    if (monthsCovered < 1) return '#ef4444'; // Red
    if (monthsCovered < 3) return '#f97316'; // Orange
    return '#10B981'; // Emerald
  };

  const getStatusMessage = () => {
    if (monthsCovered < 1) return "DANGER ZONE";
    if (monthsCovered < 3) return "VULNERABLE";
    return "SECURE";
  };

  return (
    <div className="max-w-4xl mx-auto px-4 pt-8 animate-fade-in pb-20">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
        <ArrowLeft size={20} />
        Back to Dashboard
      </button>

      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-red-900/30 rounded-xl text-red-500">
            <ShieldAlert size={32} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white">The Bunker</h1>
            <p className="text-slate-400 font-inter uppercase text-[10px] font-bold tracking-widest">Emergency Fund Tracker</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Controls */}
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-inter text-slate-400 uppercase mb-2 font-bold tracking-widest">Monthly Burn Rate</label>
              <div className="flex items-center gap-3 bg-slate-900 p-3 rounded-lg border border-slate-700">
                <span className="text-slate-500">R</span>
                <input 
                  type="number" 
                  value={expenses} 
                  onChange={(e) => setExpenses(Math.max(0, Number(e.target.value)))}
                  className="bg-transparent w-full text-white font-mono focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-inter text-slate-400 uppercase mb-2 font-bold tracking-widest">Current Cash Stash</label>
              <div className="flex items-center gap-3 bg-slate-900 p-3 rounded-lg border border-slate-700">
                <span className="text-slate-500">R</span>
                <input 
                  type="number" 
                  value={savings} 
                  onChange={(e) => setSavings(Math.max(0, Number(e.target.value)))}
                  className="bg-transparent w-full text-white font-mono focus:outline-none"
                />
              </div>
            </div>

            <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700">
              <div className="text-xs font-inter text-slate-400 mb-1 uppercase tracking-widest font-bold">Survival Time</div>
              <div className="text-3xl font-mono font-bold text-white flex items-end gap-2">
                {daysCovered} <span className="text-lg text-slate-500 mb-1">Days</span>
              </div>
              <div className="text-sm font-inter text-slate-400">({monthsCovered.toFixed(1)} Months)</div>
            </div>
          </div>

          {/* Visuals */}
          <div className="flex flex-col justify-between">
             <div className="h-64 relative bg-slate-900 rounded-xl p-4 border border-slate-700 w-full">
               <ResponsiveContainer width="100%" height="100%">
                 <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                   <XAxis dataKey="name" stroke="#64748b" tick={false} />
                   <YAxis stroke="#64748b" />
                   <Tooltip 
                     cursor={{fill: 'transparent'}}
                     contentStyle={{ backgroundColor: '#0f172a', borderColor: '#334155' }}
                   />
                   <ReferenceLine y={expenses * 3} stroke="#f97316" strokeDasharray="3 3" label={{ value: '3 Months', fill: '#f97316', fontSize: 10, fontWeight: 'bold' }} />
                   <ReferenceLine y={expenses * 6} stroke="#10B981" strokeDasharray="3 3" label={{ value: '6 Months', fill: '#10B981', fontSize: 10, fontWeight: 'bold' }} />
                   <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                     <Cell fill={getStatusColor()} />
                   </Bar>
                 </BarChart>
               </ResponsiveContainer>
             </div>

             <div className="mt-4 flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: getStatusColor(), backgroundColor: `${getStatusColor()}20` }}>
                <span className="font-bold text-white tracking-widest">{getStatusMessage()}</span>
                {monthsCovered < 3 ? <BatteryLow color={getStatusColor()} /> : <BatteryFull color={getStatusColor()} />}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};